import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { toast } from "sonner";
import {
  UsersThree, LockKey, Trash, FloppyDisk, CircleNotch,
  Crown, User as UserIcon,
} from "@phosphor-icons/react";

import { useI18n } from "@/lib/i18n";
const SUB_STATUS = [
  { key: "free", label: "Free", color: "bg-slate-100 text-slate-700 border-slate-300" },
  { key: "trial", label: "Trial", color: "bg-blue-50 text-blue-700 border-blue-300" },
  { key: "pro", label: "Pro", color: "bg-emerald-50 text-emerald-700 border-emerald-300" },
  { key: "enterprise", label: "Enterprise", color: "bg-purple-50 text-purple-700 border-purple-300" },
  { key: "expired", label: "Expired", color: "bg-red-50 text-red-700 border-red-300" },
];

export default function AdminUsers() {
  const { t } = useI18n();
  const [users, setUsers] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // user object or null
  const [pwUser, setPwUser] = useState(null);
  const [newPw, setNewPw] = useState("");
  const [savingPw, setSavingPw] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [u, a] = await Promise.all([
        api.get("/admin/users"),
        api.get("/analytics/summary").catch(() => ({ data: { total: 0, events: [] } })),
      ]);
      setUsers(u.data);
      setAnalytics(a.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const saveEdit = async () => {
    if (!editing) return;
    try {
      const body = {
        name: editing.name,
        role: editing.role,
        subscription_status: editing.subscription_status || "free",
        subscription_expires_at: editing.subscription_expires_at || null,
        active: editing.active !== false,
      };
      await api.put(`/admin/users/${editing.id}`, body);
      toast.success(t("admin.updated"));
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const resetPw = async () => {
    if (!pwUser || newPw.length < 6) return toast.error(t("final.admin.password_min"));
    setSavingPw(true);
    try {
      await api.post(`/admin/users/${pwUser.id}/reset-password`, { new_password: newPw });
      toast.success(`${pwUser.email} için şifre sıfırlandı`);
      setPwUser(null); setNewPw("");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingPw(false); }
  };

  const del = async (u) => {
    if (!window.confirm(`${u.email} silinsin mi? Bu işlem geri alınamaz.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success(t("admin.deleted"));
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="admin-users-page">
      <PageHeader title={t("admin.title")} subtitle={t("admin.subtitle")}>
        <div className="inline-flex items-center gap-2 bg-blue-50 border border-blue-200 text-blue-900 text-xs font-semibold px-3 py-1.5 rounded-sm">
          <UsersThree size={14} weight="duotone" /> {t("admin.total")}: <b>{users.length}</b>
        </div>
      </PageHeader>

      <div className="p-4 sm:p-8">
        {/* Analytics widget */}
        {analytics && (
          <div className="mb-6 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="analytics-widget">
            <StatCard label={t("admin.total_interactions")} value={analytics.total || 0} color="#2563EB" />
            <StatCard label={t("admin.landing_visits")} value={countEvent(analytics.events, "landing_visit")} color="#16A34A" />
            <StatCard label={t("admin.price_clicks")} value={countEvent(analytics.events, "pricing_click")} color="#F59E0B" />
            <StatCard label={t("admin.cta_clicks")} value={countEvent(analytics.events, "cta_click")} color="#8B5CF6" />
          </div>
        )}

        <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
          {loading ? (
            <div className="p-10 text-center text-slate-400"><CircleNotch size={20} className="animate-spin inline-block mr-2" /> {t("admin.loading")}</div>
          ) : (
            <table className="w-full data-table min-w-[820px]">
              <thead>
                <tr>
                  <th>{t("admin.user")}</th>
                  <th>{t("admin.role")}</th>
                  <th>{t("admin.subscription")}</th>
                  <th>{t("admin.status")}</th>
                  <th>{t("admin.registered")}</th>
                  <th className="text-right">{t("admin.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => {
                  const sub = SUB_STATUS.find((s) => s.key === (u.subscription_status || "free")) || SUB_STATUS[0];
                  return (
                    <tr key={u.id} data-testid={`user-row-${u.id}`}>
                      <td>
                        <div className="flex items-center gap-2">
                          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#2563EB] to-emerald-500 flex items-center justify-center font-bold text-white text-xs">
                            {(u.name || u.email || "?").split(/\s+/).map((s) => s[0]).slice(0, 2).join("").toUpperCase()}
                          </div>
                          <div>
                            <div className="font-semibold text-sm">{u.name || "—"}</div>
                            <div className="text-xs text-slate-500 font-mono">{u.email}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        {u.role === "admin" ? (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold bg-slate-900 text-white px-2 py-0.5 rounded-sm">
                            <Crown size={11} weight="fill" className="text-amber-400" /> Admin
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs text-slate-600">
                            <UserIcon size={11} weight="duotone" /> {t("admin.role_user")}
                          </span>
                        )}
                      </td>
                      <td>
                        <span className={`inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded-sm border ${sub.color}`}>
                          {sub.label}
                        </span>
                        {u.subscription_expires_at && (
                          <div className="text-[10px] text-slate-500 font-mono mt-0.5">
                            {t("final.admin.end")}: {String(u.subscription_expires_at).slice(0, 10)}
                          </div>
                        )}
                      </td>
                      <td>
                        {u.active === false
                          ? <span className="text-xs text-red-600 font-semibold">Pasif</span>
                          : <span className="text-xs text-emerald-700 font-semibold">{t("admin.active")}</span>}
                      </td>
                      <td className="text-xs text-slate-500 font-mono">{u.created_at?.slice(0, 10)}</td>
                      <td className="text-right">
                        <div className="inline-flex gap-1">
                          <button onClick={() => setEditing({ ...u })}
                            data-testid={`btn-edit-user-${u.id}`}
                            className="p-1.5 text-slate-600 hover:text-[#2563EB] hover:bg-blue-50 rounded-sm" title={t("admin.edit")}>
                            <FloppyDisk size={14} />
                          </button>
                          <button onClick={() => { setPwUser(u); setNewPw(""); }}
                            data-testid={`btn-reset-pw-${u.id}`}
                            className="p-1.5 text-slate-600 hover:text-amber-600 hover:bg-amber-50 rounded-sm" title={t("admin.reset_password")}>
                            <LockKey size={14} />
                          </button>
                          <button onClick={() => del(u)}
                            data-testid={`btn-delete-user-${u.id}`}
                            className="p-1.5 text-slate-600 hover:text-red-600 hover:bg-red-50 rounded-sm" title="Sil">
                            <Trash size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {users.length === 0 && (
                  <tr><td colSpan={6} className="text-center py-8 text-slate-400">{t("admin.no_users")}</td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ------- Edit Modal ------- */}
      {editing && (
        <Modal onClose={() => setEditing(null)} title={`${t("final.admin.edit")}: ${editing.email}`} testid="edit-user-modal">
          <div className="space-y-3">
            <F label={t("admin.full_name")}>
              <input data-testid="edit-user-name" className="input" value={editing.name || ""}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
            </F>
            <F label={t("admin.role")}>
              <select data-testid="edit-user-role" className="input" value={editing.role}
                onChange={(e) => setEditing({ ...editing, role: e.target.value })}>
                <option value="user">{t("admin.role_user")}</option>
                <option value="admin">{t("admin.role_admin")}</option>
              </select>
            </F>
            <F label={t("admin.subscription_status")}>
              <select data-testid="edit-user-sub" className="input" value={editing.subscription_status || "free"}
                onChange={(e) => setEditing({ ...editing, subscription_status: e.target.value })}>
                {SUB_STATUS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </F>
            <F label={t("admin.subscription_end")}>
              <input data-testid="edit-user-sub-expires" type="date" className="input"
                value={(editing.subscription_expires_at || "").slice(0, 10)}
                onChange={(e) => setEditing({ ...editing, subscription_expires_at: e.target.value || null })} />
            </F>
            <label className="flex items-center gap-2 text-sm">
              <input data-testid="edit-user-active" type="checkbox" className="accent-[#2563EB]"
                checked={editing.active !== false}
                onChange={(e) => setEditing({ ...editing, active: e.target.checked })} />
              Hesap aktif
            </label>
            <div className="flex justify-end gap-2 pt-3 border-t border-slate-200">
              <button onClick={() => setEditing(null)} className="px-4 py-2 text-sm border border-slate-300 rounded-sm hover:bg-slate-50">{t("final.admin.cancel")}</button>
              <button onClick={saveEdit} data-testid="btn-save-user"
                className="px-4 py-2 text-sm font-semibold bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-sm">{t("final.admin.save")}</button>
            </div>
          </div>
        </Modal>
      )}

      {/* ------- Manual Admin Password Reset Modal ------- */}
      {pwUser && (
        <Modal onClose={() => setPwUser(null)} title={`${t("final.admin.reset")}: ${pwUser.email}`} testid="reset-pw-modal">
          <div className="space-y-3">
            <div className="text-sm text-slate-600">{t("admin.reset_info")}</div>
            <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 p-2.5">
              Bu alan yalnızca yönetici destek müdahalesi içindir. Normal kullanıcılar “Şifremi Unuttum” üzerinden e-posta bağlantısıyla kendi şifrelerini sıfırlar.
            </div>
            <F label={t("admin.new_password")}>
              <input data-testid="admin-new-pw" type="password" className="input" value={newPw}
                onChange={(e) => setNewPw(e.target.value)} minLength={6} placeholder="En az 6 karakter" />
            </F>
            <div className="flex justify-end gap-2 pt-3 border-t border-slate-200">
              <button onClick={() => setPwUser(null)} className="px-4 py-2 text-sm border border-slate-300 rounded-sm hover:bg-slate-50">{t("final.admin.cancel")}</button>
              <button onClick={resetPw} disabled={savingPw || newPw.length < 6}
                data-testid="btn-confirm-reset-pw"
                className="px-4 py-2 text-sm font-semibold bg-amber-500 hover:bg-amber-600 disabled:opacity-40 text-white rounded-sm inline-flex items-center gap-1">
                <LockKey size={14} weight="bold" /> {savingPw ? t("admin.assigning") : t("admin.assign")}
              </button>
            </div>
          </div>
        </Modal>
      )}

      <style>{`.input{width:100%;border:1px solid #cbd5e1;padding:0.5rem 0.75rem;font-size:0.875rem;font-family:'JetBrains Mono',monospace;outline:none;border-radius:2px}.input:focus{border-color:#2563EB;box-shadow:0 0 0 3px rgba(37,99,235,0.15)}`}</style>
    </div>
  );
}

function F({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase font-semibold tracking-wider text-slate-500 mb-1">{label}</div>
      {children}
    </label>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-3">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
      <div className="mt-1 font-display font-extrabold text-2xl" style={{ color }}>{value}</div>
    </div>
  );
}

function countEvent(events, eventName) {
  if (!events) return 0;
  return events.filter((e) => e.event === eventName).reduce((s, e) => s + (e.count || 0), 0);
}

function Modal({ title, onClose, children, testid }) {
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid={testid}>
      <div className="bg-white w-full max-w-md border border-slate-300 rounded-sm" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
          <h3 className="font-display font-bold text-sm uppercase tracking-wider text-slate-800">{title}</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900">✕</button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}
